import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext();

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [token, setToken] = useState(null);
  const [admin, setAdmin] = useState(null);
  const [user, setUser] = useState(null);
  const [userType, setUserType] = useState(null); // 'admin' | 'user'
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    const storedUserType = localStorage.getItem('userType');
    if (storedToken) {
      setToken(storedToken);
      setUserType(storedUserType || 'admin');
      verifyToken(storedToken);
    } else {
      setLoading(false);
    }
  }, []);

  const verifyToken = async (tokenToVerify) => {
    try {
      const response = await axios.get(`${API_URL}/auth/verify`, {
        headers: { Authorization: `Bearer ${tokenToVerify}` }
      });
      if (response.data.valid) {
        setIsAuthenticated(true);
        setToken(tokenToVerify);
        const type = response.data.userType || 'admin';
        setUserType(type);
        localStorage.setItem('userType', type);
        axios.defaults.headers.common['Authorization'] = `Bearer ${tokenToVerify}`;
        if (type === 'user') {
          setAdmin(null);
          try {
            const meRes = await axios.get(`${API_URL}/auth/me`);
            setUser(meRes.data);
          } catch {
            setUser({ id: response.data.userId });
          }
        } else {
          setUser(null);
          setAdmin(response.data.admin || { id: response.data.adminId });
        }
      } else {
        logout();
      }
    } catch (error) {
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (username, password) => {
    try {
      const response = await axios.post(`${API_URL}/auth/login`, {
        username,
        password
      });
      const { token: t, admin: a, userType: type } = response.data;
      localStorage.setItem('token', t);
      localStorage.setItem('userType', type || 'admin');
      setToken(t);
      setAdmin(a || null);
      setUser(null);
      setUserType(type || 'admin');
      setIsAuthenticated(true);
      axios.defaults.headers.common['Authorization'] = `Bearer ${t}`;
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Login failed'
      };
    }
  };

  const loginWithOtp = async (mobile, otp) => {
    try {
      const response = await axios.post(`${API_URL}/auth/otp-verify`, { mobile, otp });
      const { token: t, user: u, userType: type } = response.data;
      localStorage.setItem('token', t);
      localStorage.setItem('userType', type || 'user');
      setToken(t);
      setUser(u || null);
      setAdmin(null);
      setUserType(type || 'user');
      setIsAuthenticated(true);
      axios.defaults.headers.common['Authorization'] = `Bearer ${t}`;
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Invalid OTP or mobile'
      };
    }
  };

  const requestOtp = async (mobile) => {
    try {
      await axios.post(`${API_URL}/auth/otp-request`, { mobile });
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to send OTP'
      };
    }
  };

  const requestSignupOtp = async (mobile) => {
    try {
      await axios.post(`${API_URL}/auth/signup-otp-request`, { mobile });
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to send OTP'
      };
    }
  };

  const signupWithOtp = async (mobile, otp) => {
    try {
      const response = await axios.post(`${API_URL}/auth/signup-otp-verify`, { mobile, otp });
      const { token: t, user: u, userType: type } = response.data;
      localStorage.setItem('token', t);
      localStorage.setItem('userType', type || 'user');
      setToken(t);
      setUser(u || null);
      setAdmin(null);
      setUserType(type || 'user');
      setIsAuthenticated(true);
      axios.defaults.headers.common['Authorization'] = `Bearer ${t}`;
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Signup failed'
      };
    }
  };

  const refreshUser = async () => {
    if (userType !== 'user' || !token) return;
    try {
      const meRes = await axios.get(`${API_URL}/auth/me`);
      setUser(meRes.data);
    } catch (_) {}
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userType');
    setToken(null);
    setAdmin(null);
    setUser(null);
    setUserType(null);
    setIsAuthenticated(false);
    delete axios.defaults.headers.common['Authorization'];
  };

  return (
    <AuthContext.Provider value={{
      isAuthenticated,
      token,
      admin,
      user,
      userType,
      login,
      loginWithOtp,
      requestOtp,
      requestSignupOtp,
      signupWithOtp,
      refreshUser,
      logout,
      loading
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

